import { Component, Input, OnChanges } from '@angular/core';
import { formatDate, formatNumber } from '@angular/common';
import { PhoneFormatPipe } from '../shared.service';

// One step of the call script.
interface ScriptNode {
  step: number;
  phase: string;
  locked?: boolean;
  cue?: string;
  say: string;
  next?: string;
}

/**
 * Step-by-step call script card. Used inside the outgoing-call modal and in the
 * "Fetch Script" popup on the debt details page, so both always show the same script.
 */
@Component({
  selector: 'app-call-script',
  templateUrl: './call-script.component.html',
  styleUrl: './call-script.component.scss'
})
export class CallScriptComponent implements OnChanges {
  @Input() patientName: any;
  @Input() phoneNumber: any;
  @Input() practiceName: any;
  @Input() treatmentAmount: any;
  @Input() displayLogo: any;
  @Input() debtDuration: any;

  readonly TOTAL_STEPS = 5;
  readonly stepList = [1, 2, 3, 4, 5];
  currentNode = 'identity';
  scriptNodes: { [key: string]: ScriptNode } = {};

  ngOnChanges(): void {
    this.buildScript();
  }

  buildScript() {
    this.scriptNodes = {
      identity: {
        step: 1, phase: 'Identity check', next: 'miranda',
        cue: 'Pause here. Wait for a clear yes before anything else.',
        say: 'Hello, may I please confirm I’m speaking with {name}?'
      },
      miranda: {
        step: 2, phase: 'Mini-Miranda', locked: true, next: 'details',
        cue: 'Read it exactly. Do not shorten it or put it in your own words.',
        say: 'This is a communication from a debt collector. This is an attempt to collect a debt, and any information obtained will be used for that purpose.'
      },
      details: {
        step: 3, phase: 'Collection details', next: 'consent',
        cue: 'Quote only the figures on the account in front of you.',
        say: this.hasAccountDetails()
          ? 'Your account with {creditor} has a balance of {balance}, overdue since {overdue}. I’d like to help you get this resolved today. What would work for you?'
          : 'I’m calling about your account balance. I’d like to help you get this resolved today. What would work for you?'
      },
      consent: {
        step: 4, phase: 'Text-message consent', next: 'close',
        cue: 'Read the number back digit by digit before you ask.',
        // No number is known outside a live call, so the read-back is left out there.
        say: 'To help you stay on top of your account, we can send you payment reminders and account updates by text message to this number' +
          (this.phoneNumber ? ', {number}' : '') +
          '. Do we have your permission to contact you by text at this number? Standard message and data rates may apply.'
      },
      close: {
        step: 5, phase: 'Close',
        cue: 'The validation notice must go out within five days. No further contact until it does.',
        say: 'Thank you for your time today.'
      }
    };
  }

  // Step 3 quotes the account figures only when all of them are known.
  hasAccountDetails(): boolean {
    return !!(this.practiceName && this.treatmentAmount != null && this.debtDuration);
  }

  get scriptNode(): ScriptNode {
    return this.scriptNodes[this.currentNode];
  }

  // Replaces {slot} placeholders with highlighted values; values are escaped before use as innerHTML.
  fillScript(text: string): string {
    const slots: { [key: string]: string } = {
      name: this.patientName || 'the customer',
      number: new PhoneFormatPipe().transform(this.phoneNumber),
      creditor: this.practiceName,
      balance: (this.displayLogo || '') + formatNumber(Number(this.treatmentAmount) || 0, 'en-US', '1.2-2'),
      // Overdue-from date is a calendar date, so format it in UTC to avoid a day shift
      overdue: this.debtDuration ? formatDate(this.debtDuration, 'MMMM d, y', 'en-US', 'UTC') : ''
    };
    return String(text).replace(/\{(\w+)\}/g, (_, k) =>
      '<b class="slot">' + this.escapeHtml(slots[k] || k) + '</b>');
  }

  escapeHtml(value: string): string {
    return String(value ?? '').replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as any)[c]);
  }

  nextStep() {
    if (this.scriptNode.next) this.currentNode = this.scriptNode.next;
  }

  // The step whose Next leads to the current one; undefined on the first step.
  get prevNode(): string | undefined {
    return Object.keys(this.scriptNodes).find(k => this.scriptNodes[k].next === this.currentNode);
  }

  prevStep() {
    const prev = this.prevNode;
    if (prev) this.currentNode = prev;
  }
}
